import { useEffect, useState } from 'react'
import { fetchControlInsight, fetchInsightJobRuns } from '../api/control'
import { resolveAccent, bandColor } from './accentColors'
import BlockRenderer from './BlockRenderer'
import Icon from './Icon'
import styles from './InsightDetailModal.module.css'

function formatRunDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// Local chrome glyphs, matching the convention in SkillDetailsModal/SourceDetailsModal
// (modal chrome icons live next to the modal, not in the shared data-driven Icon set).
function CloseIcon() {
  return <Icon name="close" />
}

function ArrowLeftIcon() {
  return <Icon name="arrow-left" />
}

function ArrowRightIcon() {
  return <Icon name="arrow-right" />
}

/**
 * InsightDetailModal — drill-down overlay opened from a Home insight
 * card's "View details" link. Self-fetching: loads every ranked
 * opportunity for the active insight job run on mount, defaulting to the
 * card's run (its latest). The left rail switches between opportunities;
 * each opportunity's content is a set of typed-block sections (Why this
 * ranks, Problem definition, ...) grouped server-side into tabs and
 * navigated via the top tabs / Previous-Next footer controls — all
 * client-side against the one payload, no per-tab refetch. A date selector
 * at the top of the rail (shown once the job's run history is known) lets
 * the user switch to an older completed run, which refetches the whole
 * payload for that run.
 *
 * Props:
 *   card    {{id, rank, accent, title, subtitle}} — the Home card this
 *           modal was opened from.
 *   onClose {function}
 */
export default function InsightDetailModal({ card, onClose }) {
  const [data, setData] = useState(null)
  const [opportunityIndex, setOpportunityIndex] = useState(0)
  const [sectionIndex, setSectionIndex] = useState(0)
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeRunId, setActiveRunId] = useState(card.id)
  const [runs, setRuns] = useState([])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    fetchControlInsight(activeRunId)
      .then(d => { if (active) { setData(d); setOpportunityIndex(0); setSectionIndex(0); setShowAnalysis(false) } })
      .catch(e => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [activeRunId])

  // Fires once per modal open (the job id is stable across a job's runs),
  // not on every date-selector switch.
  useEffect(() => {
    if (!data?.insightJobId) return
    let active = true
    fetchInsightJobRuns(data.insightJobId)
      .then(r => { if (active) setRuns(r) })
      .catch(() => { /* run history is a non-critical enhancement */ })
    return () => { active = false }
  }, [data?.insightJobId])

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose()
  }

  function selectOpportunity(i) {
    setOpportunityIndex(i)
    setSectionIndex(0)
    setShowAnalysis(false)
  }

  function selectAnalysis() {
    setShowAnalysis(true)
  }

  const { text, bg, border } = resolveAccent(card.accent)
  const opportunities = data?.opportunities ?? []
  const opportunity = opportunities[opportunityIndex]
  const runBlocks = data?.runBlocks ?? []
  // Tabs arrive grouped from the backend: one {label, blocks} per section.
  const tabs = opportunity?.sections ?? []
  const activeTab = tabs[Math.min(sectionIndex, Math.max(0, tabs.length - 1))]

  function prevSection() {
    if (tabs.length === 0) return
    setSectionIndex(i => (i + tabs.length - 1) % tabs.length)
  }

  function nextSection() {
    if (tabs.length === 0) return
    setSectionIndex(i => (i + 1) % tabs.length)
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Insight details">

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading insight…</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>Failed to load: {error}</div>
        ) : (
          <>
          {/* Mobile-only persistent identity bar — desktop shows this same
              info in the rail instead (hidden there on mobile, see CSS).
              Stays put above .layout while the rail/content scroll under it.
              Reads as "Back" rather than "Close": on mobile this view takes
              over the whole screen, so leaving it is a step backward in a
              drill-down, not dismissing an overlay — same destination
              (onClose), different mental model. */}
          <div className={styles.mobileBar}>
            <button type="button" className={styles.backBtn} onClick={onClose} aria-label="Back to Control Panel">
              <ArrowLeftIcon />
              <span>Back</span>
            </button>
            <div className={styles.mobileBarText}>
              <span className={styles.mobileBarEyebrow} style={{ color: text }}>Insight {card.rank}</span>
              <span className={styles.mobileBarTitle}>{data.title}</span>
            </div>
          </div>

          <div className={styles.layout}>

            {/* ── Left rail — ranked opportunities ──────────────────── */}
            <aside className={styles.rail}>
              <div className={styles.railEyebrowRow}>
                <span className={styles.railEyebrow} style={{ color: text }}>Insight {card.rank}</span>
                {runs.length > 1 && (
                  <select
                    className={styles.runSelect}
                    value={activeRunId}
                    onChange={e => setActiveRunId(Number(e.target.value))}
                    aria-label="Select insight run date"
                  >
                    {runs.map(r => (
                      <option key={r.id} value={r.id}>{formatRunDate(r.date)}</option>
                    ))}
                  </select>
                )}
              </div>
              <span className={styles.railTitle}>{data.title}</span>
              {data.subtitle && <span className={styles.railSubtitle}>{data.subtitle}</span>}

              <span className={styles.railListLabel}>Ranked Opportunities</span>
              <div className={styles.railList}>
                {opportunities.map((op, i) => {
                  const active = i === opportunityIndex && !showAnalysis
                  const score = op.badge ?? op.value ?? op.progress
                  return (
                    <button
                      key={op.id}
                      type="button"
                      className={`${styles.railRow} ${active ? styles.railRowActive : ''}`}
                      style={active ? { backgroundColor: bg, borderColor: border } : undefined}
                      onClick={() => selectOpportunity(i)}
                    >
                      <div className={styles.railRowTop}>
                        <span
                          className={styles.railDot}
                          style={active ? { backgroundColor: text, color: '#04121e' } : undefined}
                        >
                          {op.rank}
                        </span>
                        <span className={styles.railRowLabel} style={active ? { color: 'var(--text-primary)' } : undefined}>{op.label}</span>
                      </div>
                      {typeof op.progress === 'number' && (
                        <div className={styles.railRowBottom}>
                          <span className={styles.railTrack}>
                            <span
                              className={styles.railFill}
                              style={{ width: `${Math.max(0, Math.min(100, op.progress))}%`, backgroundColor: active ? text : 'rgba(var(--overlay-base),.2)' }}
                            />
                          </span>
                          <span className={styles.railScore} style={active ? { color: text } : undefined}>{score}</span>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>

              {/* Cross-opportunity narrative, selectable like an opportunity:
                  picking it swaps the body pane to the run-level analysis
                  instead of the per-opportunity tabs. Only offered when the
                  run actually produced that narrative. */}
              {runBlocks.length > 0 && (
                <div className={styles.railAnalysis}>
                  <button
                    type="button"
                    className={`${styles.railRow} ${styles.analysisRow} ${showAnalysis ? styles.railRowActive : ''}`}
                    style={showAnalysis ? { backgroundColor: bg, borderColor: border } : undefined}
                    onClick={selectAnalysis}
                  >
                    <div className={styles.railRowTop}>
                      <span
                        className={styles.railDot}
                        style={showAnalysis ? { backgroundColor: text, color: '#04121e' } : undefined}
                      >
                        <Icon name="analytics" />
                      </span>
                      <span className={styles.railRowLabel} style={showAnalysis ? { color: 'var(--text-primary)' } : undefined}>
                        Additional Analysis
                      </span>
                    </div>
                  </button>
                </div>
              )}
              <div className={styles.railFooter}>{data.footerCaption}</div>
            </aside>

            {/* ── Right pane — active opportunity detail, or the run-level
                 Additional Analysis when its rail row is selected ───── */}
            {(showAnalysis || opportunity) && (
              <div className={styles.content}>
                {/* One ✕, pinned to the pane's upper-right corner, so it sits in
                    the identical spot whether an opportunity or the Additional
                    Analysis is showing rather than tracking each view's own
                    header. */}
                <button className={`${styles.closeBtn} ${styles.paneClose}`} onClick={onClose} aria-label="Close">
                  <CloseIcon />
                </button>
                {showAnalysis ? (
                  <div className={styles.contentBody}>
                    <h2 className={`${styles.oppTitle} ${styles.analysisTitle}`}>Additional Analysis</h2>
                    <div className={styles.blockBody}>
                      <BlockRenderer blocks={runBlocks} accentColor={text} />
                    </div>
                  </div>
                ) : (
                <>
                <div className={styles.contentHeader}>
                  <div className={styles.contentHeaderMain}>
                    <div className={styles.contentHeaderTop}>
                      <span className={styles.rankPill} style={{ color: text, backgroundColor: bg, borderColor: border }}>
                        Rank #{opportunity.rank}
                      </span>
                      {opportunity.tags?.length > 0 && (
                        <span className={styles.contentSubtitle}>{opportunity.tags.join(' · ')}</span>
                      )}
                    </div>
                    <h2 className={styles.oppTitle}>{opportunity.title}</h2>
                  </div>
                </div>

                <div className={styles.metaStrip}>
                  {opportunity.meta.map(m => (
                    <div key={m.label} className={styles.metaCell}>
                      <span className={styles.metaLabel}>{m.label}</span>
                      <span className={styles.metaValue} style={{ color: bandColor(m.color_key) ?? text }}>{m.value}</span>
                    </div>
                  ))}
                </div>

                <div className={styles.sectionTabs}>
                  {tabs.map((t, i) => {
                    const active = i === sectionIndex
                    return (
                      <button
                        key={i}
                        type="button"
                        className={styles.sectionTab}
                        style={{
                          borderBottomColor: active ? text : 'transparent',
                          color: active ? 'var(--text-primary)' : undefined,
                          fontWeight: active ? 650 : 400,
                        }}
                        onClick={() => setSectionIndex(i)}
                      >
                        {t.label}
                      </button>
                    )
                  })}
                </div>

                {activeTab && (
                  <div className={styles.contentBody}>
                    <div className={styles.sectionHeading}>
                      <span className={styles.sectionEyebrow} style={{ color: text }}>{activeTab.label.toUpperCase()}</span>
                      <span className={styles.sectionRule} />
                      <span className={styles.sectionPosition}>Section {sectionIndex + 1} of {tabs.length}</span>
                    </div>
                    <div key={`${opportunity.id}-${sectionIndex}`} className={styles.blockBody}>
                      <BlockRenderer blocks={activeTab.blocks} accentColor={text} />
                    </div>
                  </div>
                )}
                </>
                )}
              </div>
            )}
          </div>
          </>
        )}

        {/* ── Footer — per-opportunity only; the Additional Analysis view
             has no sections to page through, so it carries no footer. Close
             lives in the pane's upper-right ✕, not here. ─────────────── */}
        {!loading && !error && opportunity && !showAnalysis && (
          <div className={styles.footer}>
            <span className={styles.footerCaption}>
              Derived from {opportunity.executions} executions · {opportunity.contacts} affected contacts · {data.updatedLabel}
            </span>
            <div className={styles.footerSpacer} />
            <button type="button" className={styles.footerNavBtn} onClick={prevSection}>
              <ArrowLeftIcon />
              Previous section
            </button>
            <button type="button" className={styles.footerNavBtn} onClick={nextSection}>
              Next section
              <ArrowRightIcon />
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
